import React from 'react';
import { Space, Tag, Avatar, Tooltip, Button } from 'antd';
import { UserOutlined, EditOutlined, TeamOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Member } from '../../../services/organizationAPI';

// 角色颜色映射
const ROLE_COLORS: Record<string, string> = {
    owner: 'gold',
    admin: 'blue',
    member: 'default',
};

// 在职状态颜色
const STATUS_COLORS: Record<string, string> = {
    active: 'green',
    probation: 'orange',
    resigned: 'red',
};

interface MemberTableColumnsProps {
    members: Member[];
    onDetail: (memberId: number) => void;
}

export const useMemberTableColumns = ({ members, onDetail }: MemberTableColumnsProps): ColumnsType<Member> => {
    const getSupervisorName = (supervisorUserId: number | null | undefined) => {
        if (!supervisorUserId) return null;
        const supervisor = members.find(m => m.user_id === supervisorUserId);
        return supervisor?.username;
    };

    return [
        {
            title: '员工',
            key: 'user',
            render: (_, record) => {
                const fullName = (record.last_name || '') + (record.first_name || '');
                return (
                    <Space>
                        <Avatar icon={<UserOutlined />} src={record.avatar} />
                        <div>
                            <div style={{ fontWeight: 500 }}>
                                {fullName || record.username}
                            </div>
                            <div style={{ fontSize: 12, color: '#666' }}>
                                {record.username}
                                {record.job_title && ` · ${record.job_title}`}
                            </div>
                        </div>
                    </Space>
                );
            },
        },
        {
            title: '邮箱',
            dataIndex: 'email',
            key: 'email',
            ellipsis: true,
        },
        {
            title: '部门',
            dataIndex: 'department_name',
            key: 'department_name',
            render: (text) => text || <Tag>未分配</Tag>,
        },
        {
            title: '直属上级',
            key: 'supervisor',
            render: (_, record) => {
                const supervisorName = getSupervisorName(record.supervisor);
                return supervisorName ? (
                    <Space>
                        <TeamOutlined />
                        <span>{supervisorName}</span>
                    </Space>
                ) : (
                    <Tag color="orange">无上级</Tag>
                );
            },
        },
        {
            title: '状态',
            key: 'employee_status',
            render: (_, record) => {
                const status = record.employee_status || 'active';
                const labels: Record<string, string> = {
                    active: '在职',
                    probation: '试用期',
                    resigned: '离职'
                };
                return <Tag color={STATUS_COLORS[status]}>{labels[status]}</Tag>;
            },
        },
        {
            title: '角色',
            dataIndex: 'role',
            key: 'role',
            render: (role: string, record) => (
                <Tag color={ROLE_COLORS[role]}>{record.role_display}</Tag>
            ),
        },
        {
            title: '操作',
            key: 'action',
            width: 100,
            render: (_, record) => (
                <Tooltip title="查看/编辑档案">
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => onDetail(record.id)}
                    >
                        档案
                    </Button>
                </Tooltip>
            ),
        },
    ];
};
